import { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { SiteLayout, PageHeader } from "../layouts/SiteLayout";
import { BRAND } from "../data/siteContent";
import { CONTACT_T } from "../constants/siteTestIds";
import { Seo } from "../seo/Seo";
import { buildSchemas, webPageSchema, breadcrumbSchema } from "../seo/schemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: "media", label: "Media", email: BRAND.emails.media, tid: CONTACT_T.catMedia, blurb: "For journalists, editors and producers. Interviews, profiles, op-eds." },
  { key: "podcast", label: "Podcast Guests", email: BRAND.emails.podcast, tid: CONTACT_T.catPodcast, blurb: "Pitch yourself or your client as a guest on The Prove Your Point Podcast." },
  { key: "business", label: "Business Partnerships", email: BRAND.emails.business, tid: CONTACT_T.catBusiness, blurb: "Brand partnerships, sponsorships and venture conversations." },
  { key: "general", label: "General Inquiries", email: BRAND.emails.general, tid: CONTACT_T.catGeneral, blurb: "Everything else. Manuel reads every message." },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: "media",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.length < 10) {
      toast.error("Please fill name, email, and a message of 10+ characters.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/booking`, form);
      toast.success("Message received. Manuel's team will reply within 48 hours.");
      setForm({ name: "", email: "", organization: "", inquiry_type: "media", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectCategory = (key) => {
    update("inquiry_type", key);
    const el = document.getElementById("contact-form-anchor");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <SiteLayout>
      <Seo
        title="Contact Manuel Santana — Media, Podcast, Business, General"
        description="Get in touch with Manuel Santana's team. Routes for media inquiries, podcast guest pitches, business partnerships and general inquiries — typical response within 48 hours."
        path="/contact"
        schemas={buildSchemas([
          webPageSchema({ path: "/contact", name: "Contact Manuel Santana", description: "Categorized contact form for Manuel Santana." }),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
        ])}
      />
      <PageHeader
        overline="Contact"
        title="Let's talk."
        italic="Briefly &amp; well."
        kicker="Pick the lane that fits. Each one routes to the right person on Manuel's team — typically a response inside 48 hours."
      />

      <section data-testid={CONTACT_T.section} className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 lg:gap-8 mb-20">
            {CATEGORIES.map((c, i) => (
              <motion.button
                key={c.key}
                data-testid={c.tid}
                onClick={() => selectCategory(c.key)}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className={`col-span-12 md:col-span-6 lg:col-span-3 text-left border p-7 md:p-8 h-full transition-all ${
                  form.inquiry_type === c.key
                    ? "border-[#0a0a0a] bg-[#0a0a0a] text-white"
                    : "border-black/15 hover:border-[#0a0a0a]"
                }`}
              >
                <p className={`font-mono-ui text-[10px] tracking-[0.3em] uppercase ${form.inquiry_type === c.key ? "text-white/60" : "text-black/40"}`}>№ 0{i + 1}</p>
                <h3 className={`mt-5 font-display text-2xl md:text-3xl tracking-tight ${form.inquiry_type === c.key ? "text-white" : "text-[#0a0a0a]"}`}>{c.label}</h3>
                <p className={`mt-4 text-sm leading-relaxed ${form.inquiry_type === c.key ? "text-white/65" : "text-black/55"}`}>{c.blurb}</p>
                <p className={`mt-6 font-mono-ui text-[11px] tracking-[0.2em] ${form.inquiry_type === c.key ? "text-white/80" : "text-[#0a0a0a]"}`}>{c.email}</p>
              </motion.button>
            ))}
          </div>

          <div id="contact-form-anchor" className="grid grid-cols-12 gap-6 lg:gap-16">
            <div className="col-span-12 lg:col-span-4">
              <p className="font-mono-ui text-[11px] tracking-[0.3em] uppercase text-[#0a0a0a]">— Send A Message</p>
              <h3 className="mt-5 font-display text-[#0a0a0a] text-3xl md:text-4xl tracking-tight leading-tight">
                One form. <span className="font-light text-black/45">The right inbox.</span>
              </h3>
              <p className="mt-6 text-black/60 text-base leading-relaxed">
                Choose your category above and the message routes accordingly. Manuel's team responds within 48 business hours.
              </p>
            </div>

            <form data-testid={CONTACT_T.form} onSubmit={submit} className="col-span-12 lg:col-span-8 grid grid-cols-2 gap-x-8 gap-y-6">
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">Your Name *</label>
                <input data-testid={CONTACT_T.name} type="text" value={form.name} onChange={(e) => update("name", e.target.value)} className="terminal-input" placeholder="First & Last" required />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">Email *</label>
                <input data-testid={CONTACT_T.email} type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="terminal-input" placeholder="you@company.com" required />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">Organization</label>
                <input data-testid={CONTACT_T.org} type="text" value={form.organization} onChange={(e) => update("organization", e.target.value)} className="terminal-input" placeholder="Company / outlet" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">Category</label>
                <select data-testid={CONTACT_T.type} value={form.inquiry_type} onChange={(e) => update("inquiry_type", e.target.value)} className="terminal-input bg-white appearance-none">
                  {CATEGORIES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="font-mono-ui text-[10px] tracking-[0.3em] uppercase text-black/40">Your message *</label>
                <textarea data-testid={CONTACT_T.message} value={form.message} onChange={(e) => update("message", e.target.value)} className="terminal-input" placeholder="The shorter and clearer, the faster the response." rows={6} required />
              </div>
              <div className="col-span-2 flex justify-end mt-4">
                <button data-testid={CONTACT_T.submit} type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
                  {submitting ? "Sending..." : "Send"} <Send size={14} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
